import { combineEpics, StateObservable, ofType } from 'redux-observable';
import { Observable, of, mergeMap, map, startWith, catchError, delay } from 'rxjs';
import { ID, ReduxAction } from '@repo/utils/types';
import AppState from '../reducers/app-state';
import { apiObservable } from '@repo/utils/helpers/api';
import { requestActions } from './apiHelpers';
import { alertUser } from './alerts';
import { selectCurrentLocation } from './ui';

const duplicateActions = requestActions('DUPLICATE', ['fetchAll', 'fetch', 'delete']);
const duplicateMemberProfileActions = requestActions('DUPLICATE_MEMBER_PROFILE', ['delete']);
const duplicatesScanStatusActions = requestActions('DUPLICATES_SCAN_STATUS', ['fetch']);
const duplicatesScanActions = requestActions('DUPLICATES_SCAN', ['create']);

export const fetchDuplicates = duplicateActions['fetchAll'];
export const fetchMoreDuplicates = () => ({ type: 'FETCH_MORE_DUPLICATES' });
export const startScanForDuplicates = duplicatesScanActions['create'];
export const markScanForDuplicatesAsInProgress = () => ({
  type: 'SCAN_FOR_DUPLICATES_IN_PROGRESS',
});
export const fetchDuplicateStatus = duplicatesScanStatusActions['fetch'];

export const setSelectedDuplicate = (duplicateID: ID) => ({
  type: 'SET_SELECTED_DUPLICATE',
  payload: { id: duplicateID },
});

export const ignoreDuplicate = duplicateActions['delete'];

export const ignoreDuplicateMemberProfile = (potentialDuplicateID: ID, contactID: ID) => ({
  type: 'DELETE_DUPLICATE_MEMBER_PROFILE',
  payload: { id: potentialDuplicateID, contactID },
});

const ignoreDuplicateEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_DUPLICATE'),
    mergeMap((action) =>
      apiObservable('DELETE', `/duplicates/${action.payload}/`, {}, { apiVersion: 'v2' }).pipe(
        map((response) => duplicateActions['deleteSucceeded']({ ...response, ...action.payload })),
      ),
    ),
  );

const scanForDuplicatesEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_DUPLICATES_SCAN'),
    mergeMap(() =>
      apiObservable('POST', '/duplicates/scan/', {}, { apiVersion: 'v2' }).pipe(
        map(() => duplicatesScanActions['createSucceeded']()),
      ),
    ),
  );

const pollForDuplicatesScanCompletionEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType(
      'FETCH_DUPLICATES_SCAN_STATUS',
      'CREATE_DUPLICATES_SCAN_SUCCEEDED',
      'SCAN_FOR_DUPLICATES_IN_PROGRESS',
    ),
    mergeMap(({ type }) =>
      apiObservable('GET', '/duplicates/status/', {}, { apiVersion: 'v2' }).pipe(
        delay(type === 'FETCH_DUPLICATES_SCAN_STATUS' ? 0 : 1000 * 3),
        map(({ response }) => {
          if (response.is_syncing) {
            return markScanForDuplicatesAsInProgress();
          } else if (!response.is_syncing) {
            return duplicatesScanStatusActions['fetchSucceeded']({ ...response });
          }

          return { type: 'NO_OP' };
        }),
        catchError((err) => of(duplicatesScanStatusActions['fetchFailed'](err))),
      ),
    ),
  );

const ignoreDuplicateMemberProfileEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_DUPLICATE_MEMBER_PROFILE'),
    mergeMap(({ payload }) => {
      return apiObservable(
        'DELETE',
        `/duplicates/${payload.id}/contact/${payload.contactID}`,
        {},
        { apiVersion: 'v2' },
      ).pipe(
        map((response) =>
          duplicateMemberProfileActions['deleteSucceeded']({ ...response, ...payload }),
        ),
      );
    }),
  );

const fetchDuplicatesPageObservable = (state$: StateObservable<AppState>, page: number) =>
  apiObservable(
    'GET',
    '/duplicates/',
    {
      query: {
        limit: state$.value.data.duplicates.limit,
        page,
      },
    },
    { apiVersion: 'v2' },
  );

const projectToFetchDuplicatesPage = (state$: StateObservable<AppState>, page: number) =>
  fetchDuplicatesPageObservable(state$, page).pipe(
    map((response) => duplicateActions['fetchAllSucceeded']({ ...response, page })),
    startWith(duplicateActions['fetchAllRequested']()),
    catchError((err) => of(duplicateActions['fetchAllFailed'](err))),
  );

const fetchDuplicatesEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType('FETCH_ALL_DUPLICATES', 'FETCH_DUPLICATES_SCAN_STATUS_SUCCEEDED'),
    mergeMap(() => projectToFetchDuplicatesPage(state$, 1)),
  );

const fetchMoreDuplicatesEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_MORE_DUPLICATES'),
    mergeMap(() => projectToFetchDuplicatesPage(state$, state$.value.data.duplicates.page + 1)),
  );

const clearTransitioningOutDuplicatesEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType(
      'MERGE_CONTACT_BATCH_SUCCEEDED',
      'DELETE_DUPLICATE_MEMBER_PROFILE',
      'DELETE_CONTACT_BATCH_REQUESTED',
      'DELETE_DUPLICATE',
    ),
    delay(250),
    map(() => ({ type: 'CLEAR_TRANSITIONING_OUT_DUPLICATES' })),
  );

// not doing this for now b/c we can't currently differentiate between a user-initiated scan and a system-initiated scan
const alertUserOnDuplicateScanCompletedEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_DUPLICATES_SCAN_STATUS_SUCCEEDED'),
    map(() => {
      const { pathname } = selectCurrentLocation(state$.value);

      if (pathname?.includes('duplicates')) {
        return { type: 'NO_OP' };
      }

      return alertUser('Duplicate scan completed', 'success');
    }),
  );

const alertUserOnDuplicateIgnoredEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_DUPLICATE_SUCCEEDED'),
    map(() => alertUser('Potential duplicate ignored', 'success')),
  );

const alertUserOnDuplicateMemberProfileIgnoredEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_DUPLICATE_MEMBER_PROFILE_SUCCEEDED'),
    map(() => alertUser('Potential duplicate profile ignored', 'success')),
  );

const alertUserOnDuplicateMergedEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('MERGE_CONTACT_BATCH_SUCCEEDED'),
    map(() => alertUser('Duplicate profiles merged', 'success')),
  );

export const duplicatesEpic = combineEpics(
  fetchDuplicatesEpic,
  fetchMoreDuplicatesEpic,
  scanForDuplicatesEpic,
  pollForDuplicatesScanCompletionEpic,
  ignoreDuplicateEpic,
  ignoreDuplicateMemberProfileEpic,
  clearTransitioningOutDuplicatesEpic,
  alertUserOnDuplicateScanCompletedEpic,
  alertUserOnDuplicateIgnoredEpic,
  alertUserOnDuplicateMemberProfileIgnoredEpic,
  alertUserOnDuplicateMergedEpic,
);
